/**
 * BookingActivity.jsx — Iter-358 (P0-356)
 *
 * Standalone activity/audit page for a single booking. Deep-linkable
 * (`/admin/booking/:booking_id/activity`) so ops/support/dispute-
 * resolution can pull up the full history without navigating through
 * a shift.
 *
 * Renders:
 *   • Shift + client + talent header (venue, event time, location, tz)
 *   • Full chronological timeline (via `LifecycleTimeline` in expanded mode)
 *   • Notification delivery + audit ledger summary
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Loader2, Calendar, MapPin, Building2, User, Clock } from 'lucide-react';
import LifecycleTimeline from '../../components/LifecycleTimeline';

export default function BookingActivity() {
  const { booking_id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/attendance-state/booking/${booking_id}`);
        setState(res.data);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Failed to load booking activity');
      } finally {
        setLoading(false);
      }
    })();
  }, [booking_id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-rose-600 font-semibold" data-testid="booking-activity-error">{err}</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="booking-activity-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-slate-900">Booking Activity</h1>
            <p className="text-xs text-slate-500 font-mono">{booking_id}</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-500" /> Shift Context
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <Field icon={Building2} label="Venue" value={state?.venue_name} />
            <Field icon={User}      label="Role"  value={state?.role_name} />
            <Field icon={Calendar}  label="Date"  value={state?.shift_date} />
            <Field icon={MapPin}    label="Booking ID" value={booking_id} mono />
            <Field icon={Clock}     label="Overall Status">
              <Badge className="ml-1 bg-cyan-50 text-cyan-800 border-cyan-200" data-testid={`overall-${state?.overall_status}`}>
                {(state?.overall_status || 'unknown').replace(/_/g, ' ')}
              </Badge>
            </Field>
            <Field label="Payment Status" value={state?.payment_status} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              Full Audit Trail
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Iter-358 — always expanded on the standalone page so
                support can scan the full history without clicking. */}
            <LifecycleTimeline booking_id={booking_id} initialOpen />
          </CardContent>
        </Card>

        {/* Outstanding blockers panel */}
        {state?.blockers?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Outstanding Requirements</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {state.blockers.map((b) => (
                <div key={b.code} className="flex items-start gap-3 text-sm p-2 border border-slate-200 rounded"
                     data-testid={`blocker-${b.code}`}>
                  <Badge
                    className={
                      b.informational
                        ? 'bg-slate-100 text-slate-700 border-slate-300'
                        : 'bg-rose-50 text-rose-700 border-rose-300'
                    }
                  >
                    {b.informational ? 'Info' : 'Blocker'}
                  </Badge>
                  <div className="flex-1">
                    <div className="font-semibold">{b.label}</div>
                    <div className="text-xs text-slate-600">{b.message}</div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

function Field({ icon: Icon, label, value, mono, children }) {
  return (
    <div className="flex items-start gap-2">
      {Icon && <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />}
      <div className="min-w-0 flex-1">
        <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
        {children ? children : (
          <p className={`text-sm ${mono ? 'font-mono text-xs' : ''} text-slate-900 break-all`}>
            {value || '—'}
          </p>
        )}
      </div>
    </div>
  );
}
